import { ApolloClient, ApolloLink, gql, HttpLink, InMemoryCache, makeVar } from '@apollo/client';
import { onError } from '@apollo/client/link/error';

import config from './config';

export const isAuthenticatedVar = makeVar<boolean>(true);

export const currentGraphQLErrorVar = makeVar<{
  showError: boolean;
  headerText: string;
  bodyText: string;
}>({
  showError: false,
  headerText: '',
  bodyText: '',
});

interface Link {
  [key: string]: HttpLink;
}

interface ServerMap {
  [key: string]: string | undefined;
}

export const typeDefs = gql`
  type CurrentGraphQLError {
    showError: Boolean!
    headerText: String!
    bodyText: String!
  }

  extend type Query {
    isAuthenticated: Boolean!
    currentGraphQLError: CurrentGraphQLError!
  }
`;

const serverMap: ServerMap = {
  apps: config.APPS_SERVER,
  hi: config.HI_SERVER,
  hpd: config.HPD_SERVER,
  pii: config.PII_SERVER,
  pii_public: config.PII_PUBLIC_SERVER,
};

const links: Link = Object.entries(serverMap).reduce((accumulator, [key, value]) => {
  return {
    ...accumulator,
    [key]: new HttpLink({
      uri: value,
      credentials: 'include',
    }),
  };
}, {});

const generateSplitLink = (index = 0): ApolloLink => {
  const id: string = Object.keys(links)[index];
  const link = links[id];
  const numLinks = Object.keys(links).length;

  return ApolloLink.split(
    (operation: any) => operation.getContext().clientName === id,
    link,
    numLinks === index + 1 ? links[index + 1] : generateSplitLink(index + 1),
  );
};

export const errorLink = onError(({ graphQLErrors, networkError }: any) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }: any) => {
      console.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`);
      currentGraphQLErrorVar({
        showError: true,
        headerText: '',
        bodyText: message,
      });
    });
  }

  if (networkError) {
    switch (networkError.statusCode) {
      case 401:
      case 403:
        isAuthenticatedVar(false);
        break;

      default:
        console.log(`[Network error]: ${networkError}`);
    }
  }
});

const client = new ApolloClient({
  link: ApolloLink.from([errorLink, generateSplitLink()]),
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          isAuthenticated: {
            read() {
              return isAuthenticatedVar();
            },
          },
          currentGraphQLError: {
            read() {
              return currentGraphQLErrorVar();
            },
          },
        },
      },
    },
  }),
  typeDefs,
});

export { client };
